// Mesón · fase 1 (docs/frontend/18): COBRAR una nota guardada (F10) = recibo de caja con su pago
// anticipado. Rápido y por teclado: el foco parte en el monto (ya con el saldo), Enter cobra. Un
// cobro puede juntar medios (parte efectivo, parte débito: «+ otro medio», Alt+O). Efectivo calcula el
// vuelto (no se guarda: el recibo registra lo abonado). Tarjeta en terminal Getnet: código de
// autorización del voucher (y cuotas si es crédito). Cheque: banco, número y fecha. Transferencia: N°
// de operación. Nunca más que el saldo: lo decide el backend igual (PAGO_EXCEDE_NOTA).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type KeyboardEvent } from 'react';
import { api } from '../api/cliente';
import { claves, useTurnoActual } from '../api/consultas';
import type { MedioCaja, PagoNota, ReciboRegistrado } from '../api/contratos';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { entero, filaNueva, medioDe, NOMBRE_MEDIO, problemaDe, type Fila } from '../comun/medios';

/** Fase 2a: lo que entra físicamente al mesón exige el turno de caja abierto (la transferencia no). */
const FISICOS: readonly MedioCaja[] = ['EFECTIVO', 'DEBITO', 'CREDITO', 'CHEQUE'];

/**
 * Lo aplicado NETO del recibo (aplicaciones menos reversas): un recibo cuya aplicación se reversó
 * vuelve a estar libre, y por lo tanto se puede anular (Teneduría de pagos, 2026-10-28: antes la
 * pantalla lo escondía por tener filas, aunque el servidor lo aceptaba).
 */
const aplicadoNeto = (r: PagoNota['recibos'][number]) =>
  r.aplicaciones.reduce(
    (s, a) => s + (a.tipo === 'APLICACION' ? 1 : -1) * Number(a.monto || 0),
    0,
  );

export function CobrarNota({
  empresaId,
  clienteEntidadId,
  pago,
  puedeAnular,
  puedeOperarCaja = false,
  alCobrado,
  alCerrar,
}: {
  empresaId: string;
  clienteEntidadId: string;
  pago: PagoNota;
  puedeAnular: boolean;
  /** Fase 2a: con `caja.turno.operar` se muestra el turno (o su falta) y se valida antes de cobrar. */
  puedeOperarCaja?: boolean;
  /** Punto de venta: quien abrió el cobro sigue con la boleta cuando la nota queda pagada. */
  alCobrado?: (r: ReciboRegistrado) => void;
  alCerrar: () => void;
}) {
  const qc = useQueryClient();
  const intentos = useIntentos();
  // Sin el permiso de operar caja no se consulta (call center): el backend decide igual.
  const turno = useTurnoActual(empresaId, puedeOperarCaja);
  const sinTurno = turno.isSuccess && turno.data === null;
  const saldo = Number(pago.saldo);
  const [filas, setFilas] = useState<Fila[]>([filaNueva(saldo ? String(saldo) : '')]);
  const [observacion, setObservacion] = useState('');
  const [listo, setListo] = useState<ReciboRegistrado | null>(null);
  const [anulando, setAnulando] = useState<{ id: string; version: number; motivo: string } | null>(
    null,
  );

  const cobra = filas.reduce((s, f) => s + (entero(f.monto) || 0), 0);
  const problemas = filas.map((f) =>
    sinTurno && FISICOS.includes(f.medio)
      ? 'abre tu turno de caja (o cobra por transferencia)'
      : problemaDe(f),
  );
  const excede = cobra > saldo;
  const valido = saldo > 0 && cobra > 0 && !excede && problemas.every((p) => !p);
  const poner = (i: number, c: Partial<Fila>) =>
    setFilas((fs) => fs.map((f, j) => (j === i ? { ...f, ...c } : f)));
  const otroMedio = () =>
    setFilas((fs) => [...fs, filaNueva(cobra < saldo ? String(saldo - cobra) : '', 'DEBITO')]);

  const refrescar = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: claves.pagoNota(empresaId, pago.notaId) }),
      qc.invalidateQueries({ queryKey: claves.caja(empresaId) }),
    ]);
  const cobrar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        empresaId,
        clienteEntidadId,
        notaId: pago.notaId,
        medios: filas.map(medioDe),
        ...(observacion.trim() ? { observacion: observacion.trim() } : {}),
      };
      return api.post<ReciboRegistrado>('/caja/recibos', cuerpo, {
        idempotencia: intentos.clave('cobrar-nota', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setListo(r);
      alCobrado?.(r);
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });
  const anular = useMutation({
    mutationFn: (a: { id: string; version: number; motivo: string }) =>
      api.post(
        `/caja/recibos/${a.id}/anular`,
        { motivo: a.motivo, version: a.version },
        { idempotencia: intentos.clave('anular-recibo', a) },
      ),
    onSuccess: async () => {
      intentos.cerrar();
      setAnulando(null);
      await refrescar();
    },
    onError: (e) => intentos.cerrar(e),
  });

  const enviar = () => valido && !cobrar.isPending && cobrar.mutate();
  const teclaCampo = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      enviar();
    } else if (e.altKey && (e.key === 'o' || e.key === 'O')) {
      e.preventDefault();
      otroMedio();
    }
  };

  if (listo)
    return (
      // key propia: es OTRO diálogo (se monta de nuevo y el foco va a «Listo»; Enter cierra).
      <Modal
        key="listo"
        titulo={`Cobro registrado · ${pago.nota}`}
        ancho="angosto"
        alCerrar={alCerrar}
      >
        <div className="cobro-listo" data-recibo={listo.numeroVisible}>
          <p className="cobro-numero mono">{listo.numeroVisible}</p>
          <p>
            {pesos(listo.total)} ·{' '}
            {listo.estadoPago === 'PAGADA' ? 'la nota quedó PAGADA' : 'pago parcial registrado'}
            {listo.turno ? ` · turno ${listo.turno}` : ''}
          </p>
          <p className="ayuda">
            El pago viaja con la nota: lo ven bodega, despacho y distribución, y se aplica solo a la
            factura o boleta cuando se emita.
          </p>
          <button type="button" className="primario" data-autofoco onClick={alCerrar}>
            Listo <kbd>Enter</kbd>
          </button>
        </div>
      </Modal>
    );

  return (
    <Modal
      titulo={`Cobrar · ${pago.nota} (F10)`}
      ancho="medio"
      ubicacion="media"
      alCerrar={alCerrar}
      pie={
        <>
          <span className={excede ? 'texto-malo' : 'apagado'} data-cobro-resumen>
            {excede
              ? `Supera el saldo en ${pesos(String(cobra - saldo))}`
              : `Cobra ${pesos(String(cobra))} · queda ${pesos(String(Math.max(saldo - cobra, 0)))}`}
          </span>
          <button type="button" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="primario"
            disabled={!valido || cobrar.isPending}
            onClick={enviar}
          >
            {cobrar.isPending ? 'Cobrando…' : 'Cobrar'} <kbd>Enter</kbd>
          </button>
        </>
      }
    >
      <div className="cobro">
        {turno.isSuccess && (
          <p
            className={sinTurno ? 'aviso-linea tono-aviso' : 'apagado'}
            data-turno-cobro={turno.data?.numeroVisible ?? 'sin-turno'}
          >
            {turno.data
              ? `Turno ${turno.data.numeroVisible} · ${turno.data.punto.codigo}`
              : 'Sin turno de caja abierto: solo puedes cobrar por transferencia. Ábrelo en Caja › Mi turno de caja.'}
          </p>
        )}
        <div className="cobro-cifras">
          <div>
            <span className="etiqueta-c21">Total nota</span>
            <span className="mono">{pesos(pago.total)}</span>
          </div>
          <div>
            <span className="etiqueta-c21">Pagado</span>
            <span className="mono">{pesos(pago.pagado)}</span>
          </div>
          <div className="cobro-saldo">
            <span className="etiqueta-c21">Saldo por cobrar</span>
            <span className="mono fuerte">{pesos(pago.saldo)}</span>
          </div>
        </div>

        {saldo > 0 ? (
          <table className="cobro-medios" aria-label="Medios de pago">
            <tbody>
              {filas.map((f, i) => {
                const vuelto = f.medio === 'EFECTIVO' ? entero(f.entrega) - entero(f.monto) : NaN;
                return (
                  <tr key={i} data-medio={f.medio}>
                    <td>
                      <select
                        aria-label={`Medio ${i + 1}`}
                        value={f.medio}
                        onChange={(e) => poner(i, { medio: e.target.value as MedioCaja })}
                        onKeyDown={teclaCampo}
                      >
                        {(Object.keys(NOMBRE_MEDIO) as MedioCaja[]).map((m) => (
                          <option key={m} value={m}>
                            {NOMBRE_MEDIO[m]}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        aria-label={`Monto ${i + 1}`}
                        className="celda-num fuerte"
                        inputMode="numeric"
                        value={f.monto}
                        {...(i === 0 ? { 'data-autofoco': true } : {})}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => poner(i, { monto: e.target.value })}
                        onKeyDown={teclaCampo}
                      />
                    </td>
                    <td className="cobro-detalle">
                      {f.medio === 'EFECTIVO' && (
                        <>
                          <input
                            aria-label={`Entrega ${i + 1}`}
                            className="celda-num"
                            inputMode="numeric"
                            placeholder="entrega (opcional)"
                            value={f.entrega}
                            onChange={(e) => poner(i, { entrega: e.target.value })}
                            onKeyDown={teclaCampo}
                          />
                          {vuelto > 0 && (
                            <span className="cobro-vuelto" data-vuelto>
                              Vuelto <strong className="mono">{pesos(String(vuelto))}</strong>
                            </span>
                          )}
                        </>
                      )}
                      {(f.medio === 'DEBITO' || f.medio === 'CREDITO') && (
                        <>
                          <input
                            aria-label={`Autorización ${i + 1}`}
                            placeholder="código de autorización (voucher)"
                            value={f.autorizacion}
                            onChange={(e) => poner(i, { autorizacion: e.target.value })}
                            onKeyDown={teclaCampo}
                          />
                          {f.medio === 'CREDITO' && (
                            <input
                              aria-label={`Cuotas ${i + 1}`}
                              className="celda-num cobro-cuotas"
                              inputMode="numeric"
                              placeholder="cuotas"
                              value={f.cuotas}
                              onChange={(e) => poner(i, { cuotas: e.target.value })}
                              onKeyDown={teclaCampo}
                            />
                          )}
                        </>
                      )}
                      {f.medio === 'TRANSFERENCIA' && (
                        <input
                          aria-label={`Operación ${i + 1}`}
                          placeholder="N° de operación"
                          value={f.referencia}
                          onChange={(e) => poner(i, { referencia: e.target.value })}
                          onKeyDown={teclaCampo}
                        />
                      )}
                      {f.medio === 'CHEQUE' && (
                        <>
                          <input
                            aria-label={`Banco ${i + 1}`}
                            placeholder="banco"
                            value={f.banco}
                            onChange={(e) => poner(i, { banco: e.target.value })}
                            onKeyDown={teclaCampo}
                          />
                          <input
                            aria-label={`N° cheque ${i + 1}`}
                            placeholder="N° cheque"
                            value={f.numero}
                            onChange={(e) => poner(i, { numero: e.target.value })}
                            onKeyDown={teclaCampo}
                          />
                          <input
                            aria-label={`Fecha cheque ${i + 1}`}
                            type="date"
                            value={f.fecha}
                            onChange={(e) => poner(i, { fecha: e.target.value })}
                            onKeyDown={teclaCampo}
                          />
                        </>
                      )}
                      {problemas[i] && f.monto && (
                        <span className="apagado cobro-falta">falta: {problemas[i]}</span>
                      )}
                    </td>
                    <td>
                      {filas.length > 1 && (
                        <button
                          type="button"
                          className="boton-icono"
                          aria-label={`Quitar medio ${i + 1}`}
                          onClick={() => setFilas((fs) => fs.filter((_, j) => j !== i))}
                        >
                          ✕
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p className="ayuda">La nota ya está pagada: no queda saldo por cobrar.</p>
        )}
        {saldo > 0 && (
          <div className="cobro-acciones">
            <button type="button" onClick={otroMedio} disabled={filas.length >= 6}>
              + Otro medio <kbd>Alt+O</kbd>
            </button>
            <input
              aria-label="Observación del cobro"
              placeholder="observación (opcional)"
              value={observacion}
              maxLength={500}
              onChange={(e) => setObservacion(e.target.value)}
              onKeyDown={teclaCampo}
            />
          </div>
        )}
        {cobrar.isError && <PanelError error={cobrar.error} />}

        {pago.recibos.length > 0 && (
          <table className="cobro-recibos" aria-label="Recibos de la nota">
            <thead>
              <tr>
                <th>Recibo</th>
                <th>Medios</th>
                <th className="al-der">Monto</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {pago.recibos.map((r) => (
                <tr
                  key={r.id}
                  data-recibo={r.numeroVisible}
                  className={r.estado === 'ANULADO' ? 'apagado' : ''}
                >
                  <td className="mono">
                    {r.numeroVisible}
                    <span className="apagado"> · {fecha(r.creadoEn)}</span>
                  </td>
                  <td>
                    {r.medios
                      .map(
                        (m) =>
                          `${NOMBRE_MEDIO[m.medio]} ${pesos(m.monto)}${m.autorizacion ? ` (aut. ${m.autorizacion})` : ''}`,
                      )
                      .join(' + ')}
                  </td>
                  <td className="al-der mono">{pesos(r.total)}</td>
                  <td>
                    {r.estado === 'ANULADO' ? (
                      <span className="insignia tono-malo" title={r.motivoAnulacion ?? ''}>
                        anulado
                      </span>
                    ) : aplicadoNeto(r) > 0 ? (
                      <span
                        className="insignia tono-ok"
                        title={r.aplicaciones
                          .map(
                            (a) =>
                              `${a.origen === 'AUTOMATICA' ? 'Automática' : 'Manual'} · DTE ${a.tipoDte ?? ''} N° ${a.folio ?? 's/f'} · ${pesos(a.monto)} · ${fecha(a.aplicadoEn)}`,
                          )
                          .join('\n')}
                      >
                        aplicado
                      </span>
                    ) : (
                      <span className="insignia tono-info">vigente</span>
                    )}
                  </td>
                  <td>
                    {puedeAnular &&
                      r.estado === 'VIGENTE' &&
                      aplicadoNeto(r) === 0 &&
                      (anulando?.id === r.id ? (
                        <span className="cobro-anular">
                          <input
                            aria-label="Motivo de la anulación"
                            data-autofoco
                            placeholder="motivo"
                            value={anulando.motivo}
                            onChange={(e) => setAnulando({ ...anulando, motivo: e.target.value })}
                          />
                          <button
                            type="button"
                            className="peligro"
                            disabled={anulando.motivo.trim().length < 3 || anular.isPending}
                            onClick={() => anular.mutate(anulando)}
                          >
                            Anular
                          </button>
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAnulando({ id: r.id, version: r.version, motivo: '' })}
                        >
                          Anular…
                        </button>
                      ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {anular.isError && <PanelError error={anular.error} />}
      </div>
    </Modal>
  );
}
